import React from 'react';
import { Folder, FileText, FileCode, Image, Music, Package } from 'lucide-react';
import { BuildSettings, BuildPlatform, formatBuildSize } from '../../../../core/types/build.types';
import { cn } from '../../../utils/cn';

interface Props {
  settings: BuildSettings;
  platform: BuildPlatform;
}

interface PreviewFile {
  name: string;
  type: 'folder' | 'html' | 'js' | 'css' | 'image' | 'audio' | 'json';
  size: number;
  children?: PreviewFile[];
}

const FileIcon: React.FC<{ type: PreviewFile['type']; className?: string }> = ({ type, className }) => {
  switch (type) {
    case 'folder':
      return <Folder size={14} className={cn('text-yellow-400', className)} />;
    case 'html':
      return <FileText size={14} className={cn('text-orange-400', className)} />;
    case 'js':
      return <FileCode size={14} className={cn('text-yellow-300', className)} />;
    case 'css':
      return <FileCode size={14} className={cn('text-blue-400', className)} />;
    case 'image':
      return <Image size={14} className={cn('text-green-400', className)} />;
    case 'audio':
      return <Music size={14} className={cn('text-purple-400', className)} />;
    case 'json':
      return <FileText size={14} className={cn('text-zinc-400', className)} />;
    default:
      return <FileText size={14} className={cn('text-zinc-400', className)} />;
  }
};

const FileTreeItem: React.FC<{ file: PreviewFile; level?: number }> = ({ file, level = 0 }) => {
  return (
    <div>
      <div
        className="flex items-center gap-2 py-1 px-2 hover:bg-zinc-700/50 rounded text-sm"
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        <FileIcon type={file.type} />
        <span className="text-zinc-300 flex-1">{file.name}</span>
        <span className="text-zinc-500 text-xs">{formatBuildSize(file.size)}</span>
      </div>
      {file.children?.map((child, index) => (
        <FileTreeItem key={index} file={child} level={level + 1} />
      ))}
    </div>
  );
};

export const BuildPreview: React.FC<Props> = ({ settings, platform }) => {
  // Generate estimated output structure based on settings
  const generatePreviewStructure = (): PreviewFile[] => {
    const files: PreviewFile[] = [];

    // Base HTML file
    files.push({
      name: 'index.html',
      type: 'html',
      size: 2048,
    });

    // JavaScript bundle
    const jsSize = settings.minifyJs ? 256000 : 512000;
    files.push({
      name: settings.configuration === 'production' ? 'bundle.min.js' : 'bundle.js',
      type: 'js',
      size: jsSize,
    });

    // CSS
    files.push({
      name: 'styles.css',
      type: 'css',
      size: 8192,
    });

    // Assets folder
    const assetsChildren: PreviewFile[] = [];

    // Estimated images (compressed if enabled)
    const imageSize = settings.compressImages ? 512000 : 1024000;
    assetsChildren.push({
      name: 'sprites/',
      type: 'folder',
      size: imageSize,
      children: [
        { name: 'atlas.png', type: 'image', size: imageSize * 0.7 },
        { name: 'atlas.json', type: 'json', size: 4096 },
      ],
    });

    // Audio assets
    assetsChildren.push({
      name: 'audio/',
      type: 'folder',
      size: 256000,
      children: [
        { name: 'bgm.mp3', type: 'audio', size: 204800 },
        { name: 'sfx.mp3', type: 'audio', size: 51200 },
      ],
    });

    files.push({
      name: 'assets/',
      type: 'folder',
      size: imageSize + 256000,
      children: assetsChildren,
    });

    // Source maps (if enabled)
    if (settings.generateSourceMaps) {
      files.push({
        name: 'bundle.js.map',
        type: 'json',
        size: 128000,
      });
    }

    // PWA files
    if (settings.webFeatures?.includes('pwa')) {
      if (settings.manifestEnabled) {
        files.push({
          name: 'manifest.json',
          type: 'json',
          size: 1024,
        });
      }
      if (settings.serviceWorkerEnabled) {
        files.push({
          name: 'sw.js',
          type: 'js',
          size: 4096,
        });
      }
    }

    return files;
  };

  const previewFiles = generatePreviewStructure();
  const totalSize = previewFiles.reduce((sum, file) => sum + file.size, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-zinc-300">Estimated Output</h3>
        <div className="flex items-center gap-2">
          <Package size={14} className="text-zinc-500" />
          <span className="text-sm text-zinc-400">
            ~{formatBuildSize(totalSize)}
          </span>
        </div>
      </div>

      <div className="bg-zinc-900 rounded-lg border border-zinc-700 p-2 max-h-48 overflow-y-auto">
        <div className="font-mono text-xs">
          {/* Root folder */}
          <div className="flex items-center gap-2 py-1 px-2 text-sm font-medium text-zinc-200">
            <Folder size={14} className="text-blue-400" />
            <span>{settings.outputDirectory || './dist'}/</span>
          </div>

          {/* File tree */}
          {previewFiles.map((file, index) => (
            <FileTreeItem key={index} file={file} level={1} />
          ))}
        </div>
      </div>

      {/* Optimization summary */}
      <div className="flex flex-wrap gap-2">
        {settings.minifyJs && (
          <span className="px-2 py-1 text-xs bg-green-600/20 text-green-400 rounded">
            Minified
          </span>
        )}
        {settings.compressImages && (
          <span className="px-2 py-1 text-xs bg-green-600/20 text-green-400 rounded">
            Compressed
          </span>
        )}
        {settings.bundleAssets && (
          <span className="px-2 py-1 text-xs bg-green-600/20 text-green-400 rounded">
            Bundled
          </span>
        )}
        {settings.removeUnused && (
          <span className="px-2 py-1 text-xs bg-green-600/20 text-green-400 rounded">
            Tree-shaken
          </span>
        )}
        {platform === 'web' && settings.webFeatures?.includes('pwa') && (
          <span className="px-2 py-1 text-xs bg-blue-600/20 text-blue-400 rounded">
            PWA Ready
          </span>
        )}
      </div>
    </div>
  );
};
